
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from './UserContext';
import { Helmet } from 'react-helmet';

const ReferPage = () => {
  const [searchParams] = useSearchParams();
  const { signup, initialLoading, authOperationLoading } = useUser();
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    console.log('ReferPage: Mounted with token:', token);
    if (!token) {
      setMessage('Invalid or missing referral token. Please use a valid referral link.');
      return;
    }

    const validateReferral = async () => {
      setIsProcessing(true);
      try {
        const response = await fetch('https://arush1234sharma-querio-backend.hf.space/api/referral-validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ referral_token: token }),
        });
        const data = await response.json();
        if (response.ok && data.status === 'valid') {
          setMessage('Referral link is valid! Sign up below to join NexNotes AI.');
        } else {
          setMessage(data.message || 'Invalid or expired referral token.');
        }
      } catch (error) {
        console.error('ReferPage: Error validating referral token:', error);
        setMessage('Error validating referral link. Please try again later.');
      } finally {
        setIsProcessing(false);
      }
    };

    validateReferral();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('ReferPage: handleSubmit called', { email });

    if (isProcessing || authOperationLoading) {
      setMessage('Please wait, an operation is already in progress.');
      return;
    }

    if (!email.trim() || !password.trim()) {
      setMessage('Email and password are required.');
      return;
    }

    if (password.length < 8) {
      setMessage('Password must be at least 8 characters long.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address.');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await signup(email, password, { data: { referral_token: token } });
      if (response?.data?.user) {
        setMessage('Signup successful! Redirecting to app...');
        setTimeout(() => {
          console.log('ReferPage: Redirecting to /app after signup');
          navigate('/app');
        }, 1500);
      } else {
        setMessage('Signup failed. Please try again.');
      }
    } catch (error) {
      console.error('ReferPage: Signup error:', error);
      if (error.message.includes('Email not confirmed')) {
        setMessage('Please confirm your email before signing in.');
      } else if (error.message.includes('network') || error.name === 'AuthRetryableFetchError') {
        setMessage('Network error. Please check your connection and try again.');
      } else {
        setMessage(error.message || 'An unexpected error occurred.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-900 text-white">
        <div className="flex items-center space-x-2">
          <svg className="animate-spin h-5 w-5 text-blue-400" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>NexNotes AI - Join via Referral</title>
        <meta name="description" content="Join NexNotes AI using a referral link and unlock exclusive features!" />
      </Helmet>
      <div className="flex justify-center items-center h-screen bg-gray-900 text-white">
        <div className="bg-gray-800 p-8 rounded-lg w-96 shadow-lg">
          <h2 className="text-2xl font-semibold mb-4">Join via Referral</h2>
          {message && (
            <p className={`mb-3 text-sm ${message.includes('successful') || message.includes('valid') ? 'text-green-400' : 'text-red-400'}`}>
              {message}
            </p>
          )}
          {token && !message.includes('Invalid') && !message.includes('Error') && (
            <form onSubmit={handleSubmit}>
              <input
                type="email"
                placeholder="Email"
                className="w-full px-4 py-2 mb-3 rounded bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isProcessing || authOperationLoading}
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Password"
                className="w-full px-4 py-2 mb-3 rounded bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isProcessing || authOperationLoading}
                minLength={8}
                autoComplete="new-password"
              />
              <button
                type="submit"
                className={`w-full ${isProcessing || authOperationLoading ? 'bg-blue-700 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white font-semibold py-2 px-4 rounded transition-colors`}
                disabled={isProcessing || authOperationLoading}
              >
                {isProcessing || authOperationLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : 'Sign Up'}
              </button>
            </form>
          )}
          <p
            onClick={() => !isProcessing && !authOperationLoading && navigate('/signin')}
            className="mt-4 text-sm text-blue-400 hover:underline cursor-pointer select-none"
          >
            Already have an account? Sign in
          </p>
        </div>
      </div>
    </>
  );
};

export default ReferPage;
